import { defer, EMPTY, from, Observable, of, race, timer } from "rxjs"
import { catchError, delay, exhaustMap, filter, switchMap, take, takeUntil } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import * as uuid from "uuid"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import { getHttpHeaders, getLocationsAuthData, getSelectedAccountId } from "../../auth/selectors"
import { sendOperationsFailure, sendOperationsSuccess } from "../../operations/actions"
import {
    ASSIGN_USER_FORM_SUBMITTED,
    consoleDataReady,
    COPY_PRODUCT_MENU_REQUESTED,
    createActionOndyPlatformChanged,
    REMOTE_SCREENSHOT_FORM_SUBMITTED,
    REMOTE_SCREENSHOT_LOADING_SUCCESS,
    REMOTE_SCREENSHOT_START_POLLING,
    remoteScreenshotFormSubmitFailure,
    remoteScreenshotFormSubmitSuccess,
    remoteScreenshotStartPolling,
    remoteScreenshotStopPolling,
    SEND_COMMAND_FORM_SUBMITTED,
    SEND_EVENT_FORM_SUBMITTED,
    SEND_OPERATION_FORM_SUBMITTED,
    SEND_RAW_OPERATIONS_FORM_SUBMITTED,
    sendEventFailure,
    sendEventSuccess,
    SET_FEATURE_FLAG_FORM_SUBMITTED,
    SET_ONDY_PLATFORM,
} from "./actions"
import { ACCOUNT_SELECTED, AGENDA_MOUNTED } from "../../auth/actions"
import { MainMenuAgendaEnum } from "../../router/types"
import {
    eventSendRequest,
    featureFlagSendRequest,
    getOndyPlatformRequest,
    operationsSendRequest,
    setOndyPlatformRequest,
} from "./request"
import type { SendOperationFormData } from "./SendOperationForm"
import type {
    EventSendRequestData,
    FeatureFlagSendRequestData,
    OperationsSendRequestData,
} from "../../../graphql/types"
import { EventDataResponse } from "../../../graphql/types"
import { NotificationType, openNotification } from "../../uiKit/Notification/Notification"
import type { SendCommandFormData } from "./SendCommandForm"
import type { SendRawOperationsFormData } from "./SendRawOperationsForm"
import type { SetFeatureFlagFormData } from "./SetFeatureFlagForm"
import { CommandParamsTypeEnum, getConsolePollingData } from "./utils"
import type { RemoteScreenshotFormData } from "./RemoteScreenshotForm"
import { LOG_OUT_CLICKED } from "../../layouts/header/actions"
import { getConsoleLastPollingTimestamp } from "./selectors"
import type { SendEventFormData } from "./SendEventForm"
import { capitalize } from "../../../utils/general"
import { HttpHeaders } from "../../../api/types"
import { createEventRequest, requestEvent } from "../../../events/utils"
import { EventType } from "../../../events/types"
import { createActionLoadingError } from "../../data/actions"
import { getAdminAccessData } from "../../data/selectors"
import { logger } from "../../../utils/loggerWrap"
import { Action, StateObject } from "../../redux/types"
import { constructAuditData } from "../auditLog/utils/utils-payload-data"

const sendOperationEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_OPERATION_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("sendOperationEpic - form submitted", {
                data: action.payload,
            })

            const formData: SendOperationFormData = action.payload.values
            const requestData: OperationsSendRequestData = {
                operations: [
                    {
                        id: uuid.v4(),
                        type: formData.operationType,
                        recordType: formData.recordType,
                        recordId: formData.recordId,
                        params: formData.dataParams,
                    },
                ],
            }
            return defer(() => operationsSendRequest(getHttpHeaders(deps.getState()), requestData)).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Operation sent successfully")
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not send Operation",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendOperationsFailure(error)])
                })
            )
        })
    )

const sendEventEpic = (actions$: Observable<Action>, _state$: StateObservable<StateObject>, deps: EpicDependencies) =>
    actions$.pipe(
        ofType(SEND_EVENT_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("sendEventEpic - form submitted", {
                data: action.payload,
            })

            const accountId = getSelectedAccountId(deps.getState())
            const formData: SendEventFormData = action.payload.values

            if (!accountId || !formData.payload) {
                return []
            }

            const requestData: EventSendRequestData = {
                type: formData.eventType,
                accountId: accountId,
                locationId: formData.locationId,
                terminalId: formData.terminalId,
                aggregateType: formData.aggregateType,
                aggregateId: formData.aggregateId,
                payload: formData.payload,
            }
            return defer(() => eventSendRequest(getHttpHeaders(deps.getState()), requestData)).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Event sent successfully")
                    return [sendEventSuccess()]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not send Event",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendEventFailure(error)])
                })
            )
        })
    )

const sendCommandEpic = (actions$: Observable<Action>, _state$: StateObservable<StateObject>, deps: EpicDependencies) =>
    actions$.pipe(
        ofType(SEND_COMMAND_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("sendCommandEpic - form submitted", {
                data: action.payload,
            })

            return defer(() => {
                const formData: SendCommandFormData = action.payload.values
                const dataObj: any = {}

                if (formData.commandType === CommandParamsTypeEnum.LogSqlQuery) {
                    dataObj.query = formData.rawJson
                } else if (formData.commandType === CommandParamsTypeEnum.LogReduxState) {
                    dataObj.path = formData.rawJson
                } else if (
                    formData.commandType === CommandParamsTypeEnum.UploadMetrics ||
                    formData.commandType === CommandParamsTypeEnum.UploadLogs
                ) {
                    const parsed = formData.rawJson ? JSON.parse(formData.rawJson) : null
                    dataObj.from = parsed && parsed.from ? parsed.from : null
                    dataObj.to = parsed && parsed.to ? parsed.to : null
                } else if (formData.commandType === CommandParamsTypeEnum.RunReduxAction) {
                    dataObj.action = JSON.parse(formData.rawJson)
                } else if (formData.commandType === CommandParamsTypeEnum.StopDataDownload) {
                    dataObj.types = formData.rawJson ? JSON.parse(formData.rawJson) : []
                }

                const requestData: OperationsSendRequestData = {
                    terminalIds: formData.terminalIds,
                    operations: [
                        {
                            id: uuid.v4(),
                            type: "command",
                            params: JSON.stringify({
                                type: formData.commandType,
                                ...dataObj,
                            }),
                        },
                    ],
                }
                const headers: HttpHeaders = {
                    ...getHttpHeaders(deps.getState()),
                    ...(formData.accountId ? { ["twbrds-account-id"]: formData.accountId } : {}),
                }
                return operationsSendRequest(headers, requestData)
            }).pipe(
                switchMap(() => {
                    if (action.payload.values.commandType === CommandParamsTypeEnum.ReportTerminalInfo) {
                        openNotification(
                            NotificationType.Success,
                            "Requesting terminal(s) info...",
                            "If terminal is active, info will be displayed in few moments",
                            10
                        )
                    } else {
                        openNotification(NotificationType.Success, "Command sent successfully")
                    }
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not send Command",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendOperationsFailure(error)])
                })
            )
        })
    )

const sendRawOperationsEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEND_RAW_OPERATIONS_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("sendRawOperationsEpic - form submitted", {
                data: action.payload,
            })

            const formData: SendRawOperationsFormData = action.payload.values
            let jsonData

            try {
                jsonData = JSON.parse(formData.dataParams)
            } catch (error) {
                openNotification(
                    NotificationType.Error,
                    "Could not send parse array of operations",
                    "Please check entered JSON data"
                )
                return [sendOperationsFailure(error)]
            }

            const requestData: OperationsSendRequestData = {
                operations: jsonData.map(operation => ({
                    ...operation,
                    ...(operation.params
                        ? {
                              params: JSON.stringify(operation.params),
                          }
                        : {}),
                })),
            }
            return defer(() => operationsSendRequest(getHttpHeaders(deps.getState()), requestData)).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Raw Operations sent successfully")
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not send Raw Operations",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendOperationsFailure(error)])
                })
            )
        })
    )

const setFeatureFlagEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_FEATURE_FLAG_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("setFeatureFlagEpic - form submitted", {
                data: action.payload,
            })

            const formData: SetFeatureFlagFormData = action.payload.values
            const requestData: FeatureFlagSendRequestData = {
                key: formData.settingsKey,
                value: formData.settingsValue,
                auditData: constructAuditData(deps),
            }
            return defer(() => featureFlagSendRequest(getHttpHeaders(deps.getState()), requestData)).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Feature Flag set successfully")
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not send set Feature Flag",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendOperationsFailure(error)])
                })
            )
        })
    )

const assignFormSubmittedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ASSIGN_USER_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("assignFormSubmittedEpic - form submitted", {
                data: action.payload,
            })

            const accId = getSelectedAccountId(deps.getState())

            if (!accId) {
                return []
            }
            const adminAccess = getAdminAccessData(deps.getState())
            const authLocationIds = getLocationsAuthData(deps.getState())?.map(l => l.id) ?? []

            if (action.payload.assign) {
                return defer(() =>
                    requestEvent(
                        getHttpHeaders(deps.getState()),
                        createEventRequest(
                            EventType.AccountUserAddRequested,
                            {
                                correlationId: deps.idgen.next(),
                                accountId: accId,
                            },
                            {
                                id: deps.idgen.next(),
                                email: action.payload.email,
                                accountId: accId,
                                accessLevelId: adminAccess?.id ?? undefined,
                                roleId: action.payload.adminRoleId ?? undefined,
                                locationIds: authLocationIds,
                                firstName: "Figure",
                                lastName: "Service",
                            }
                        )
                    )
                ).pipe(
                    switchMap((res: EventDataResponse) => {
                        const id = res?.id
                        if (!id) {
                            openNotification(NotificationType.Error, "Could not assign the user.")
                            return []
                        }
                        openNotification(NotificationType.Success, "User assigned successfully.")
                        return []
                    }),
                    catchError(err => {
                        const errFormatted = err?.toString() || "Something went wrong, please reload and try again."
                        openNotification(NotificationType.Error, errFormatted)
                        return of(createActionLoadingError(err))
                    })
                )
            } else {
                return defer(() =>
                    requestEvent(
                        getHttpHeaders(deps.getState()),
                        createEventRequest(
                            EventType.AccountUserRemoveRequested,
                            {
                                correlationId: deps.idgen.next(),
                                accountId: accId,
                            },
                            {
                                email: action.payload.email,
                            }
                        )
                    )
                ).pipe(
                    switchMap((res: EventDataResponse) => {
                        const id = res?.id
                        if (!id) {
                            openNotification(NotificationType.Error, "Could not un-assign the user.")
                            return []
                        }
                        openNotification(NotificationType.Success, "User un-assigned successfully.")
                        return []
                    }),
                    catchError(err => {
                        const errFormatted = err?.toString() || "Something went wrong, please reload and try again."
                        openNotification(NotificationType.Error, errFormatted)
                        return of(createActionLoadingError(err))
                    })
                )
            }
        })
    )

const remoteScreenshotEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(REMOTE_SCREENSHOT_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("remoteScreenshotEpic - form submitted", {
                data: action.payload,
            })

            const formData: RemoteScreenshotFormData = action.payload.values
            return defer(() => {
                // Todo send event in correct structure
                const requestData: OperationsSendRequestData = {
                    terminalIds: [formData.terminalId],
                    operations: [
                        {
                            id: uuid.v4(),
                            type: "command",
                            params: JSON.stringify({
                                type: "ScreenshotRequested",
                                requestedAt: formData.requestedAt,
                                name: formData.name,
                            }),
                        },
                    ],
                }
                const headers: HttpHeaders = {
                    ...getHttpHeaders(deps.getState()),
                    ["twbrds-account-id"]: formData.accountId,
                }
                logger.info("REMOTE_SCREENSHOT_FORM_SUBMITTED - operationsSendRequest", {
                    requestData: requestData,
                })
                return operationsSendRequest(headers, requestData)
            }).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Remote screenshot requested")
                    return [sendOperationsSuccess([]), remoteScreenshotStartPolling(formData.imagePollingUrl)]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not start capturing Remote Screenshot",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([
                        sendOperationsFailure(error),
                        remoteScreenshotFormSubmitFailure("Could not start capturing Remote Screenshot"),
                    ])
                })
            )
        })
    )

const copyProductMenuEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(COPY_PRODUCT_MENU_REQUESTED),
        exhaustMap((action: Action) => {
            logger.info("copyProductMenuEpic - form submitted", {
                data: action.payload,
            })

            const sourceAccountId = action.payload.sourceAccountId

            if (!sourceAccountId) {
                openNotification(NotificationType.Error, "Could copy product menu", "Account to copy from not selected")
                return []
            }

            const targetAccountId = getSelectedAccountId(deps.getState())

            if (!targetAccountId) {
                openNotification(NotificationType.Error, "Could copy product menu", "Account to copy to not selected")
                return []
            }

            if (sourceAccountId === targetAccountId) {
                openNotification(
                    NotificationType.Error,
                    "Could copy product menu",
                    "Account to copy to is the same as account to copy from"
                )
                return []
            }

            return defer(() => {
                // Todo send event in correct structure
                const requestData: OperationsSendRequestData = {
                    operations: [
                        {
                            id: uuid.v4(),
                            type: "AppendProductMenuRequested",
                            params: JSON.stringify({
                                sourceAccountId: sourceAccountId,
                                targetAccountId: targetAccountId,
                            }),
                        },
                    ],
                }
                return operationsSendRequest(getHttpHeaders(deps.getState()), requestData)
            }).pipe(
                switchMap(() => {
                    openNotification(NotificationType.Success, "Product Menu Append Requested")
                    return [sendOperationsSuccess([])]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not request product menu appending",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendOperationsFailure(error)])
                })
            )
        })
    )

export const pollRemoteScreenshotEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(REMOTE_SCREENSHOT_START_POLLING),
        switchMap((action: Action) => {
            logger.info("pollRemoteScreenshotEpic - form submitted", {
                data: action.payload,
            })

            const url = action.payload.imagePollingUrl

            return race(
                actions$.pipe(
                    ofType(REMOTE_SCREENSHOT_LOADING_SUCCESS),
                    filter((a: Action) => a.payload.imageUrl === url),
                    take(1)
                ),
                timer(60 * 1000)
            ).pipe(
                take(1),
                takeUntil(actions$.pipe(ofType(LOG_OUT_CLICKED, ACCOUNT_SELECTED))),
                switchMap(result => {
                    logger.info("REMOTE_SCREENSHOT_START_POLLING - result", {
                        result: result,
                    })
                    if (typeof result === "number") {
                        throw new Error("Timed Out")
                    }

                    openNotification(NotificationType.Success, "Successfully captured Remote Screenshot")
                    return [
                        remoteScreenshotStopPolling(url),
                        sendOperationsSuccess([]),
                        remoteScreenshotFormSubmitSuccess(url),
                    ]
                }),
                catchError(err => {
                    const msg =
                        err.message === "Timed Out"
                            ? "Screenshot wasn't uploaded in the expected 60 seconds. Please try again..."
                            : "Something went wrong, check request response in Network tab or try again later..."
                    openNotification(NotificationType.Error, "Could not capture Remote Screenshot", msg)
                    return of(
                        remoteScreenshotStopPolling(url),
                        sendOperationsFailure(err),
                        remoteScreenshotFormSubmitFailure("Could not capture Remote Screenshot")
                    )
                })
            )
        })
    )

export const consolePollingEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGENDA_MOUNTED),
        filter((action: Action) => action.payload.type === MainMenuAgendaEnum.ConsoleSettings),
        switchMap(() =>
            timer(2000, 5000).pipe(
                takeUntil(
                    actions$.pipe(
                        ofType(AGENDA_MOUNTED),
                        filter((action: Action) => action.payload.type !== MainMenuAgendaEnum.ConsoleSettings)
                    )
                ),
                switchMap(() =>
                    getConsolePollingData(
                        getHttpHeaders(deps.getState()),
                        getConsoleLastPollingTimestamp(deps.getState())
                    )
                ),
                switchMap(result => {
                    const data = (result.content || []).filter(d => d.type !== "TerminalServiceActionRequested")

                    if (data.length === 0) {
                        return []
                    }

                    return [consoleDataReady(data, data[data.length - 1].timestamp)]
                }),
                catchError(() => EMPTY)
            )
        )
    )

const getOndyPlatformEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGENDA_MOUNTED),
        filter((action: Action) => action.payload.type === MainMenuAgendaEnum.ConsoleSettings),
        delay(200),
        switchMap(() => getOndyPlatformRequest(getHttpHeaders(deps.getState()))),
        switchMap(platform => [createActionOndyPlatformChanged(platform)])
    )

const setOndyPlatformEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SET_ONDY_PLATFORM),
        switchMap((action: Action) => {
            logger.info("setOndyPlatformEpic - form submitted", {
                data: action.payload,
            })

            return defer(() => setOndyPlatformRequest(getHttpHeaders(deps.getState()), action.payload)).pipe(
                switchMap((response: any) => {
                    openNotification(
                        NotificationType.Success,
                        `Ondy platform changed to ${capitalize(response.ondyPlatform)}`
                    )
                    return [createActionOndyPlatformChanged(response.ondyPlatform)]
                }),
                catchError(error => {
                    openNotification(
                        NotificationType.Error,
                        "Could not set Ondy platform",
                        "Something went wrong, check request response in Network tab or try again later..."
                    )
                    return from([sendEventFailure(error)])
                })
            )
        })
    )

export default [
    sendOperationEpic,
    sendCommandEpic,
    sendEventEpic,
    sendRawOperationsEpic,
    setFeatureFlagEpic,
    assignFormSubmittedEpic,
    remoteScreenshotEpic,
    pollRemoteScreenshotEpic,
    consolePollingEpic,
    copyProductMenuEpic,
    getOndyPlatformEpic,
    setOndyPlatformEpic,
]
